import React from 'react';
import { Box, useScreenClass } from '@vp/swan';
import { ReplaceTokens, useTranslations } from '@vp/om-translations';
import PropTypes from 'prop-types';
import { API_STATUS } from '../../../constants/apiStatus';

/**
 * Returns dynamic copy prompting the user to initiate an order transfer,
 * and then indicating the status of that transfer with respect to the number of orders attempted
 */
export const AnonymousOrderTransferBannerText = ({
  orderTransferStatus,
  numberOfAnonOrders,
  numberOfAnonOrdersTransferred,
}) => {
  const screenClass = useScreenClass();

  const [orderTransferBannerMessages, sharedActionMessages] = useTranslations([
    'orderTransferBannerMessages',
    'sharedActionMessages',
  ]);

  const numberOfOtherGuestOrders = numberOfAnonOrders - 1;

  // If a transfer hasn't been attempted yet, prompt the user with the option to transfer
  // this order along with any other orders they placed in that session
  if (orderTransferStatus === API_STATUS.IDLE) {
    if (numberOfOtherGuestOrders > 1) {
      return (
        <>
          <Box>
            {orderTransferBannerMessages.orderPlacedAsGuest}{' '}
            <ReplaceTokens
              tokenizedString={
                orderTransferBannerMessages.multipleGuestOrdersFound
              }
              substitutions={{ numberOfOrders: numberOfOtherGuestOrders }}
            />
          </Box>
          <Box pt={['xs'].includes(screenClass) ? 5 : 0}>
            {orderTransferBannerMessages.multipleGuestOrderTransferPrompt}
          </Box>
        </>
      );
    }
    if (numberOfOtherGuestOrders === 1) {
      return (
        <>
          <Box>
            {orderTransferBannerMessages.orderPlacedAsGuest}{' '}
            {orderTransferBannerMessages.oneOtherGuestOrderFound}
          </Box>
          <Box pt={['xs'].includes(screenClass) ? 5 : 0}>
            {orderTransferBannerMessages.multipleGuestOrderTransferPrompt}
          </Box>
        </>
      );
    }
    return (
      <Box>
        {orderTransferBannerMessages.orderPlacedAsGuest}{' '}
        {orderTransferBannerMessages.singleGuestOrderTransferPrompt}
      </Box>
    );
  }

  // If the transfer is in progress, show a loading message
  if (orderTransferStatus === API_STATUS.PENDING) {
    return orderTransferBannerMessages.guestOrderTransferProcessingText;
  }

  // If the transfer failed, show an error message :(
  if (orderTransferStatus === API_STATUS.FAILED) {
    if (numberOfOtherGuestOrders > 0) {
      return (
        <Box>
          {orderTransferBannerMessages.multipleGuestOrderTransferFailed}{' '}
          {sharedActionMessages.pleaseTryAgain}
        </Box>
      );
    }

    return (
      <Box>
        {orderTransferBannerMessages.singleGuestOrderTransferFailed}{' '}
        {sharedActionMessages.pleaseTryAgain}
      </Box>
    );
  }

  // If the transfer succeeded, show a success message :)
  if (orderTransferStatus === API_STATUS.SUCCESS) {
    const numberOfOtherGuestOrdersTransferred =
      numberOfAnonOrdersTransferred - 1;

    if (numberOfAnonOrdersTransferred > 2) {
      return (
        <>
          <Box>
            {orderTransferBannerMessages.singleGuestOrderTransferSucceeded}{' '}
            <ReplaceTokens
              tokenizedString={
                orderTransferBannerMessages.multipleGuestOrderTransferSucceeded
              }
              substitutions={{
                numberOfOrders: numberOfOtherGuestOrdersTransferred,
              }}
            />
          </Box>
          <Box pt={['xs'].includes(screenClass) ? 5 : 0}>
            {orderTransferBannerMessages.multipleOrdersInOrderHistory}
          </Box>
        </>
      );
    }
    if (numberOfAnonOrdersTransferred === 2) {
      return (
        <>
          <Box>
            {orderTransferBannerMessages.singleGuestOrderTransferSucceeded}{' '}
            {orderTransferBannerMessages.oneOtherGuestOrderTransferSucceeded}
          </Box>
          <Box pt={['xs'].includes(screenClass) ? 5 : 0}>
            {orderTransferBannerMessages.multipleOrdersInOrderHistory}
          </Box>
        </>
      );
    }

    return (
      <Box>
        {orderTransferBannerMessages.singleGuestOrderTransferSucceeded}{' '}
        {orderTransferBannerMessages.singleOrderInOrderHistory}
      </Box>
    );
  }

  return null;
};

AnonymousOrderTransferBannerText.propTypes = {
  orderTransferStatus: PropTypes.string.isRequired,
  numberOfAnonOrders: PropTypes.number.isRequired,
  numberOfAnonOrdersTransferred: PropTypes.number.isRequired,
};
